import Image from "next/image";

import cn from "classnames";

import {
  ButtonLink,
  resolveCtaLink,
} from "@/components/buttonLink/buttonLink.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { GetHomePageQueryResult } from "@/sanity.types";

export const AdtImageHero = ({ block }: { block: ImageHeroBlock }) => {
  const desktopUrl = block.backgroundImage?.url;
  const mobileUrl = block.mobileImage?.url;
  const hasPrimaryCta = resolveCtaLink(block) != null;
  const hasSecondaryCta =
    resolveCtaLink({
      ctaType: "link",
      ctaLabel: block.secondaryCtaLabel,
      ctaLink: block.secondaryCtaLink,
    }) != null;

  return (
    <section
      data-testid="adt-image-hero"
      aria-labelledby="adt-image-hero-title"
      className="bg-greyscale-headline relative isolate flex min-h-140 w-full items-center overflow-hidden text-white sm:min-h-155 lg:min-h-172.5">
      {mobileUrl ? (
        <Image
          src={mobileUrl}
          alt={block.mobileImage?.alt ?? block.backgroundImage?.alt ?? ""}
          fill
          priority
          sizes="100vw"
          className={cn("-z-20 object-cover", { "lg:hidden": desktopUrl })}
        />
      ) : null}
      {desktopUrl ? (
        <Image
          src={desktopUrl}
          alt={block.backgroundImage?.alt ?? ""}
          fill
          priority
          sizes="100vw"
          className={cn("-z-20 object-cover", { "max-lg:hidden": mobileUrl })}
        />
      ) : null}

      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(90deg,rgba(0,0,0,0.52)_0%,rgba(0,0,0,0.3)_46%,rgba(0,0,0,0.08)_100%)]"
      />

      <div className="container w-full px-6 py-16 sm:px-10 lg:px-14">
        <div className="max-w-3xl">
          {block.eyebrow ? (
            <Text
              variant="preHeaderMd"
              component="p"
              className="text-white uppercase">
              {block.eyebrow}
            </Text>
          ) : null}

          <Text
            variant="h3"
            component="h1"
            id="adt-image-hero-title"
            className="mt-4 text-white">
            {block.title}
          </Text>

          {block.legal ? (
            <div
              data-testid="adt-image-hero-legal"
              className="legal mt-6 max-w-xl text-white [&_a]:underline">
              <RichText>{block.legal}</RichText>
            </div>
          ) : null}

          {hasPrimaryCta || hasSecondaryCta ? (
            <div
              data-testid="adt-image-hero-ctas"
              className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
              {hasPrimaryCta ? (
                <ButtonLink
                  cta={block}
                  theme="heroPrimary"
                  className="w-fit px-8 py-4"
                />
              ) : null}
              {hasSecondaryCta ? (
                <ButtonLink
                  cta={{
                    ctaType: "link",
                    ctaLabel: block.secondaryCtaLabel,
                    ctaLink: block.secondaryCtaLink,
                  }}
                  theme="heroSecondary"
                  className="w-fit px-8 py-4"
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

type ImageHeroBlock = Extract<
  NonNullable<NonNullable<GetHomePageQueryResult>["pageBuilder"]>[number],
  { _type: "adtImageHero" }
> & {
  secondaryCtaLabel?: string | null;
  secondaryCtaLink?: {
    url?: string | null;
    queryString?: string | null;
    linkType?: string | null;
    anchorId?: string | null;
    openInNewTab?: boolean | null;
  } | null;
};
